import type { Subscription } from '@brighterly/lib-core-types/subscription/interfaces'
import type { PaymentRetryError, SubscriptionPauseEventProps, SubscriptionStore } from './interfaces'

import { PauseDuration, PauseReason, SubscriptionStatus } from '@brighterly/lib-core-types/subscription/enums'

import { isString } from '@shared/lib/format'
import { isFullArray } from '@shared/lib/collections'
import { toAnalyticsId } from '@shared/lib/amplitude'

import { UpcomingLessonsPauseStatus } from './enums'

const WEEK_SECONDS = 7 * 24 * 60 * 60

const pauseDurationWeeks: Record<PauseDuration, number> = {
  [PauseDuration.Weeks1]: 1,
  [PauseDuration.Weeks2]: 2,
  [PauseDuration.Weeks3]: 3,
  [PauseDuration.Weeks4]: 4,
}

const getPaymentRetryError = (state: SubscriptionStore): PaymentRetryError['error'] => {
  return state.paymentRetryData?.error || null
}

const getPaymentRetrySubscription = (state: SubscriptionStore): Subscription | null => {
  return state.paymentRetryData?.subscription || null
}

const getPaymentRetrySubscriptionId = (state: SubscriptionStore): Subscription['id'] | null => {
  return getPaymentRetrySubscription(state)?.id || null
}

const ACTIVE_STATUS_PRIORITY = [
  SubscriptionStatus.Ongoing,
  SubscriptionStatus.Paused,
  SubscriptionStatus.Retrying,
  SubscriptionStatus.Failed,
]

const getActiveSubscription = (state: SubscriptionStore): Subscription | null => {
  return ACTIVE_STATUS_PRIORITY
    .map(status => state.subscriptions.find(subscription => subscription.status === status))
    .find(Boolean) || null
}

const getPauseDetails = (state: SubscriptionStore): SubscriptionStore['pauseDetails'] => state.pauseDetails
const isPauseDetails = (state: SubscriptionStore): boolean => isString(getPauseDetails(state))

const isStatusKeepLearning = (state: SubscriptionStore): boolean => state.upcomingLessonsPauseStatus === UpcomingLessonsPauseStatus.KeepLearning
const isStatusCancelLessons = (state: SubscriptionStore): boolean => state.upcomingLessonsPauseStatus === UpcomingLessonsPauseStatus.CancelLessons

const getPauseReason = (state: SubscriptionStore): SubscriptionStore['pauseReason'] => state.pauseReason
const isPauseReasonOther = (state: SubscriptionStore): boolean => getPauseReason(state) === PauseReason.Other
const isPauseReasonServiceIssues = (state: SubscriptionStore): boolean => getPauseReason(state) === PauseReason.ServiceIssues

export const getters = {
  isLoading: (state: SubscriptionStore): SubscriptionStore['loading'] => state.loading,
  isLoaded: (state: SubscriptionStore): SubscriptionStore['loaded'] => state.loaded,
  isAvailableProductsLoading: (state: SubscriptionStore): SubscriptionStore['availableProductsLoading'] => state.availableProductsLoading,
  hasData: (state: SubscriptionStore): boolean => isFullArray(state.subscriptions),
  getSubscriptions: (state: SubscriptionStore): SubscriptionStore['subscriptions'] => state.subscriptions,
  getActiveSubscription: (state: SubscriptionStore): Subscription | null => getActiveSubscription(state),
  hasActiveSubscription: (state: SubscriptionStore): boolean => !!getActiveSubscription(state),
  getPaymentRetryData: (state: SubscriptionStore): SubscriptionStore['paymentRetryData'] => state.paymentRetryData,
  getPaymentRetryError: (state: SubscriptionStore): PaymentRetryError['error'] => getPaymentRetryError(state),
  getPaymentRetrySubscription: (state: SubscriptionStore): Subscription | null => getPaymentRetrySubscription(state),
  getPaymentRetrySubscriptionId: (state: SubscriptionStore): Subscription['id'] | null => getPaymentRetrySubscriptionId(state),
  getPaymentRetryErrorTitle: (state: SubscriptionStore): string => getPaymentRetryError(state)?.title || '',
  getPaymentRetryErrorText: (state: SubscriptionStore): string => getPaymentRetryError(state)?.text || '',
  isModalPaymentRetry: (state: SubscriptionStore): boolean => state.modalPaymentRetry,
  isBannerPaymentRetry: (state: SubscriptionStore): boolean => state.bannerPaymentRetry,
  isBannerPaymentRetryProcessing: (state: SubscriptionStore): boolean => state.bannerPaymentRetryProcessing,
  isModalPaymentRetryProcessing: (state: SubscriptionStore): boolean => state.modalPaymentRetryProcessing,
  isModalPaymentRetrySuccess: (state: SubscriptionStore): boolean => state.modalPaymentRetrySuccess,
  isBannerGroupPaymentRetry: (state: SubscriptionStore): boolean => state.bannerPaymentRetry || state.bannerPaymentRetryProcessing,
  isAutoRenew: (state: SubscriptionStore): boolean => getActiveSubscription(state)?.auto_renew || false,
  getActiveSubscriptionPace: (state: SubscriptionStore): number | null => getActiveSubscription(state)?.items?.[0]?.pace || null,
  getActiveSubscriptionPeriod: (state: SubscriptionStore): number | null => getActiveSubscription(state)?.items?.[0]?.product?.period || null,
  isModalSubscriptionPauseResume: (state: SubscriptionStore): boolean => state.modalSubscriptionPauseResume,
  isModalSubscriptionPause: (state: SubscriptionStore): boolean => !!state.modalSubscriptionPause,
  isModalSubscriptionPauseDuration: (state: SubscriptionStore): SubscriptionStore['modalSubscriptionPauseDuration'] => state.modalSubscriptionPauseDuration,
  isModalSubscriptionPauseReason: (state: SubscriptionStore): SubscriptionStore['modalSubscriptionPauseReason'] => state.modalSubscriptionPauseReason,
  getModalSubscriptionPause: (state: SubscriptionStore): Subscription | null => state.modalSubscriptionPause,
  isModalSubscriptionFurtherPayments: (state: SubscriptionStore): boolean => !!state.modalSubscriptionFurtherPayments,
  getSubscription: (state: SubscriptionStore): Subscription | null => state.subscription,
  isSubscription: (state: SubscriptionStore): boolean => !!state.subscription,
  isModalSubscriptionPauseSuccess: (state: SubscriptionStore): boolean => state.modalSubscriptionPauseSuccess,
  isPauseReasonValid: (state: SubscriptionStore): boolean => !!state.pauseDuration,
  getPauseSubscriptionData: (state: SubscriptionStore) => {
    const obj = {
      duration: state.pauseDuration,
      cancel_lessons: isStatusCancelLessons(state),
      reason: state.pauseReason,
      contact_request: state.pauseContactRequest,
    }
    if (isPauseDetails(state)) {
      return {
        ...obj,
        message: getPauseDetails(state),
      }
    } else {
      return obj
    }
  },
  getPauseEvents: (state: SubscriptionStore): SubscriptionPauseEventProps => ({
    reason: state.pauseReason ? toAnalyticsId(state.pauseReason) : null,
    duration_seconds: state.pauseDuration ? pauseDurationWeeks[state.pauseDuration] * WEEK_SECONDS : null,
    value: toAnalyticsId(state.upcomingLessonsPauseStatus),
    input_length: getPauseDetails(state)?.length || null,
  }),
  getPauseDuration: (state: SubscriptionStore): SubscriptionStore['pauseDuration'] => state.pauseDuration,
  isPauseDuration: (state: SubscriptionStore): boolean => !!state.pauseDuration,
  getPauseReason: (state: SubscriptionStore): SubscriptionStore['pauseReason'] => state.pauseReason,
  isPauseReasonOther: (state: SubscriptionStore): boolean => isPauseReasonOther(state),
  isPauseReasonServiceIssues: (state: SubscriptionStore): boolean => isPauseReasonServiceIssues(state),
  isPauseDetailsRequired: (state: SubscriptionStore): boolean => isPauseReasonOther(state) || isPauseReasonServiceIssues(state),
  isPauseReason: (state: SubscriptionStore): boolean => !!state.pauseReason,
  getPauseDetails: (state: SubscriptionStore): SubscriptionStore['pauseDetails'] => getPauseDetails(state),
  isPauseDetails: (state: SubscriptionStore): boolean => isPauseDetails(state),
  isPauseContactRequest: (state: SubscriptionStore): SubscriptionStore['pauseContactRequest'] => state.pauseContactRequest,
  getAvailableProducts: (state: SubscriptionStore): SubscriptionStore['availableProducts'] => {
    return [...state.availableProducts].sort((a, b) => b.pace - a.pace)
  },
  isAvailableProducts: (state: SubscriptionStore): boolean => isFullArray(state.availableProducts),
  getUpcomingLessonsPauseStatus: (state: SubscriptionStore): UpcomingLessonsPauseStatus => state.upcomingLessonsPauseStatus,
  isStatusKeepLearning: (state: SubscriptionStore): boolean => isStatusKeepLearning(state),
  isStatusCancelLessons: (state: SubscriptionStore): boolean => isStatusCancelLessons(state),
}
